import { Link } from "react-router";
import { ArrowLeft, ShieldCheck, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function CreateMaskDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Create New Mask in Project</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Masks are organizational units within projects that contain test structures and test
              labels. Creating masks helps organize your testing workflow by grouping related test
              structures together.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>• Your role is project manager or Admin</li>
                <li>• You must have an existing project</li>
                <li>• Appropriate permissions to create masks in the project</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Open Your Project</h3>
                <p className="text-gray-700 mb-2">
                  Navigate to the Projects page and click on the project where you want to add a
                  mask.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Go to Projects</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Navigate to Masks Section</h3>
                <p className="text-gray-700">
                  Within the project detail page, locate the masks selection section. Click the Add
                  Mask button.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Configure Mask Details</h3>
                <p className="text-gray-700 mb-2">Fill in the mask information:</p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Mask Name:</strong> A unique identifier for the mask
                  </li>
                  <li>
                    • <strong>PCM Version:</strong> Search your PCM Version in the list. If it
                    doesn't exist yet, you can choose to create it
                  </li>
                  <li>
                    • <strong>OTEST Version:</strong> The OTEST version is automatically filled in
                    and is the same as the PCM version
                  </li>
                  <li>
                    • <strong>Wafer Diameter:</strong> either 200 or 300
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                5
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Save and Continue</h3>
                <p className="text-gray-700">
                  Click "Create" to add the mask to your project. The mask will now be available
                  from the dropdown list and ready to link Design XML files.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">After creating your mask, you can:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/create-test-label">Create Test Labels</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/create-project-spec">Create Project Spec</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
          <Button asChild>
            <Link to="/projects">Start Creating Mask</Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
